import { Appearance, Message, NylasErrorResponse, Notetaker } from '@nylas/core';
import { EventEmitter } from '@stencil/core';

export type DataState = 'loading' | 'ready';

type StoresType = unknown;
type StoreInstances = {
  [K in keyof StoresType]: StoresType[K];
};
type StoreStateKeys<T> = T extends { state: infer S } ? keyof S : never;

/**
 * This type is used to create a union of all possible store state keys.
 * Example:
 * type CombinedStoreStateKeys = "nylas.messages" | "nylasTheme.colour"
 */
export type CombinedStoreStateKeys<SK extends keyof S, S = StoreInstances> = {
  [StoreKey in SK]: StoreStateKeys<S[StoreKey]> extends never | undefined ? never : `${StoreKey & string}.${StoreStateKeys<S[StoreKey]> & string}`;
}[SK];

export type InternalMessage = {
  id: string;
  data: Message;
  collapse: boolean;
  showContactData: boolean;
};

export type InternalAttachment = {
  blob: Blob;
  filename: string;
  contentType: string;
  size: number;
  messageId: string;
};

export type MethodKeys<T> = {
  [K in keyof T]: T[K] extends Function ? K : never;
}[keyof T];

// TODO: Move this to @nylas/core
export type AvailabilityTimeslot = {
  emails: string[];
  start_time: number;
  end_time: number;
  capacity?: number;
};

export type AvailabilityResponse = {
  order?: string[];
  time_slots: AvailabilityTimeslot[];
  participants: {
    email: string;
    name: string;
  }[];
  duration: number;
};

export type UISettingsResponse = {
  configuration_id: string;
  app_client_id: string;
  scheduler: {
    available_days_in_future: number;
    min_cancellation_notice: number;
    min_booking_notice: number;
    cancellation_policy: string;
    notetaker_settings?: Notetaker;
  };
  organizer: {
    name: string;
    email: string;
  };
  slug: string;
  appearance: Appearance;
  booking_type: string;
  name: string;
};

// Extracts a mapped type with keys of T that are EventEmitters, and their corresponding types
export type ExtractEventEmitterKeys<T> = {
  [K in keyof T]: T[K] extends EventEmitter<any> ? K : never;
}[keyof T];

// Extracts the event type from an EventEmitter
export type EventEmitterEventType<T> = T extends EventEmitter<infer U> ? U : never;

// Extracts a mapped type with keys of T that are EventEmitters, and their corresponding types
export type ExtractEventEmitterProperties<T> = {
  [K in keyof T]: T[K] extends EventEmitter<infer U> ? { key: K; eventType: U } : never;
}[keyof T];

export type NylasEvent = {
  booking_id: string;
  organizer: {
    email: string;
    name: string;
    is_organizer?: boolean;
  };
  title: string;
  description: string;
  status: string;
  event_id: string;
  location: string;
  booking_ref: string;
};

export type BookedEventInfo = NylasEvent & {
  start_time: string;
  end_time: string;
  timezone: string;
  email_language: string;
  additional_guests: NylasSchedulerBookingParticipant[];
  guest: NylasSchedulerBookingParticipant;
  additional_fields: Record<string, string>;
};

export type NylasSchedulerBookingParticipant = { name: string; email: string; nameReadOnly?: boolean; emailReadOnly?: boolean };

export type NylasSchedulerBookingData = {
  primaryParticipant: NylasSchedulerBookingParticipant;
  startTime?: Date;
  endTime?: Date;
  timezone?: string;
  language?: string;
  guests?: NylasSchedulerBookingParticipant[];
  additionalFields?: Record<
    string,
    {
      value: string;
      type?: string;
      readOnly?: boolean;
    }
  >;
};

/**
 * This type is used to book event and pass data to the redirect page.
 */
export type NylasSchedulerBookingDataWithFlatFields = Omit<NylasSchedulerBookingData, 'additionalFields'> & {
  additionalFields?: Record<string, string>;
  location?: string;
  booking_ref?: string;
};

export type DataResponseError = Exclude<NylasErrorResponse['error'], undefined>;
export type DataResponseReturnType<T = any> = [T, null] | [null, DataResponseError];
